<template>
  <div
    :class="inline ? '' : 'offcanvas offcanvas-fullscreen-sm-down offcanvas-end'"
    :style="inline ? 'display: contents;' : 'width: 560px; max-width: 100%;'"
    :tabindex="inline ? null : -1"
    ref="offcanvas"
    :data-bs-backdrop="inline ? null : 'static'"
    :data-bs-scroll="inline ? null : 'true'"
  >
    <!-- Header (standalone only — inline reuses parent header) -->
    <div v-if="!inline" class="offcanvas-header bg-warning py-3">
      <div>
        <h5 class="offcanvas-title fw-bold mb-0 text-dark">
          <i class="fa-solid fa-object-ungroup me-2" />
          {{ $t('Split Bill') }}
        </h5>
        <div class="small text-dark opacity-75 mt-1">
          {{ sessionName }} &nbsp;|&nbsp; {{ $t('Move items to a new bill') }}
        </div>
      </div>
      <button type="button" class="btn-close" @click="handleClose" />
    </div>

    <!-- Body -->
    <div class="offcanvas-body p-3" style="background: #f7f9fc;">

      <!-- Summary chips: Original / New (mirrors check-bill info chips) -->
      <div class="d-flex flex-wrap gap-2 mb-3">
        <div class="bg-white rounded-3 px-3 py-2 shadow-sm flex-fill" style="min-width: 160px;">
          <div class="text-muted" style="font-size: 0.7rem;">
            <i class="fa-solid fa-file-invoice me-1"></i>{{ $t('Original Bill') }}
          </div>
          <div class="d-flex align-items-baseline gap-2 mt-1">
            <span class="fw-bold" style="font-size: 0.95rem;">
              {{ $storeIndex.formatThaiCurrency(originalSubtotal) }}
            </span>
            <span class="text-muted small">
              {{ originalItemCount }} {{ $t('items') }}
            </span>
          </div>
        </div>
        <div
          class="rounded-3 px-3 py-2 shadow-sm flex-fill"
          :class="newBillItemCount > 0 ? 'bg-success bg-opacity-10 border border-success-subtle' : 'bg-white'"
          style="min-width: 160px;"
        >
          <div class="text-muted" style="font-size: 0.7rem;">
            <i class="fa-solid fa-file-circle-plus me-1"></i>{{ $t('New Bill') }}
          </div>
          <div class="d-flex align-items-baseline gap-2 mt-1">
            <span
              class="fw-bold"
              :class="{ 'text-success': newBillItemCount > 0 }"
              style="font-size: 0.95rem;"
            >
              {{ $storeIndex.formatThaiCurrency(newBillSubtotal) }}
            </span>
            <span class="small" :class="newBillItemCount > 0 ? 'text-success' : 'text-muted'">
              {{ newBillItemCount }} {{ $t('items') }}
            </span>
          </div>
        </div>
      </div>

      <!-- Quick actions -->
      <div class="d-flex gap-2 mb-3">
        <button
          type="button"
          class="btn btn-sm btn-outline-secondary rounded-3"
          :disabled="newBillItemCount === 0 || submitting"
          @click="resetAll"
        >
          <i class="fa-solid fa-rotate-left me-1"></i> {{ $t('Reset') }}
        </button>
        <button
          type="button"
          class="btn btn-sm btn-outline-success rounded-3"
          :disabled="!canMoveAll || submitting"
          @click="moveAllToNew"
        >
          <i class="fa-solid fa-angles-right me-1"></i> {{ $t('Move All') }}
        </button>
      </div>

      <!-- Items list (check-bill style) -->
      <div class="mb-3">
        <div class="d-flex align-items-center justify-content-between mb-2 px-1">
          <span class="fw-bold text-dark">
            <i class="fa-solid fa-utensils me-2 text-muted"></i>{{ $t('Order Items') }}
          </span>
          <span class="badge bg-secondary-subtle text-secondary rounded-pill">
            {{ splittableRowCount }} {{ $t('items') }}
          </span>
        </div>

        <div v-if="rows.length === 0" class="text-center text-muted py-4">
          <i class="fa-solid fa-utensils fa-2x opacity-25 mb-2"></i>
          <p class="small mb-0">{{ $t('No items in this bill') }}</p>
        </div>

        <div
          v-for="(row, index) in rows"
          :key="index"
          class="bg-white rounded-3 p-3 mb-2 shadow-sm"
          :class="{ 'opacity-50': row.cancelled, 'border border-success-subtle': row.newQty > 0 }"
        >
          <!-- Header row: qty badge + name/price + line total -->
          <div class="d-flex align-items-start gap-2">
            <span
              class="badge bg-success-subtle text-success rounded-3 fw-bold flex-shrink-0"
              style="min-width: 32px; font-size: 0.85rem;"
            >
              {{ row.totalQty }}x
            </span>
            <div class="d-flex flex-wrap align-items-baseline gap-1" style="flex: 1; min-width: 0;">
              <span class="fw-semibold" :class="{ 'text-decoration-line-through': row.cancelled }">
                {{ $t(row.name) }}
              </span>
              <span class="text-muted small">
                @{{ $storeIndex.formatThaiCurrency(row.unit_price) }}
              </span>
              <span
                v-if="row.cancelled"
                class="badge bg-danger-subtle text-danger border border-danger-subtle"
                style="font-size: 0.6rem;"
              >
                <i class="fa-solid fa-circle-xmark me-1"></i>{{ $t('Cancelled') }}
              </span>
              <span
                v-else-if="row.newQty > 0"
                class="badge bg-success text-white"
                style="font-size: 0.6rem;"
              >
                <i class="fa-solid fa-arrow-right me-1"></i>{{ $t('Moving') }} {{ row.newQty }}/{{ row.totalQty }}
              </span>
            </div>
            <div class="text-end flex-shrink-0" style="white-space: nowrap;">
              <div class="fw-bold" :class="{ 'text-decoration-line-through text-muted': row.cancelled }">
                {{ $storeIndex.formatThaiCurrency(row.unit_price * row.totalQty) }}
              </div>
            </div>
          </div>

          <!-- Detail section: indented under the qty badge -->
          <div style="padding-left: 40px;">
            <!-- Modifiers (same layout as check bill) -->
            <div
              v-if="row.modifiers && row.modifiers.length > 0"
              class="mt-2 ps-2 border-start border-2 border-light-subtle"
            >
              <div
                v-for="mod in row.modifiers"
                :key="mod.name"
                class="d-flex justify-content-between align-items-center small"
                style="font-size: 0.75rem;"
              >
                <span class="text-muted">
                  <i class="fa-solid fa-angle-right me-1 opacity-50"></i>{{ $t(mod.name) }}
                </span>
                <span v-if="mod.price > 0" class="text-theme fw-semibold ms-2">
                  +{{ $storeIndex.formatThaiCurrency(mod.price) }}
                </span>
              </div>
            </div>

            <!-- Move stepper -->
            <div
              v-if="!row.cancelled"
              class="d-flex align-items-center justify-content-between mt-2 rounded-2 px-2 py-1"
              :class="row.newQty > 0 ? 'bg-success bg-opacity-10' : 'bg-light'"
            >
              <span class="small" :class="row.newQty > 0 ? 'text-success fw-semibold' : 'text-muted'">
                <i class="fa-solid fa-file-circle-plus me-1"></i>{{ $t('Move to new bill') }}
              </span>
              <div class="d-flex align-items-center gap-2">
                <button
                  type="button"
                  class="btn btn-sm btn-outline-secondary rounded-circle d-flex align-items-center justify-content-center p-0"
                  style="width: 28px; height: 28px;"
                  :disabled="row.newQty === 0 || submitting"
                  @click="moveUnit(index, -1)"
                >
                  <i class="fa-solid fa-minus" style="font-size: 0.7rem;"></i>
                </button>
                <span class="fw-bold" style="min-width: 36px; text-align: center;">
                  {{ row.newQty }}<span class="text-muted fw-normal small"> / {{ row.totalQty }}</span>
                </span>
                <button
                  type="button"
                  class="btn btn-sm btn-success rounded-circle d-flex align-items-center justify-content-center p-0"
                  style="width: 28px; height: 28px;"
                  :disabled="row.newQty >= row.totalQty || submitting"
                  @click="moveUnit(index, 1)"
                >
                  <i class="fa-solid fa-plus" style="font-size: 0.7rem;"></i>
                </button>
              </div>
            </div>

            <div v-else class="text-muted small mt-2">
              <i class="fa-solid fa-ban me-1"></i> {{ $t('Cancelled items cannot be split') }}
            </div>
          </div>
        </div>
      </div>

    </div>

    <!-- Footer -->
    <div class="bg-white p-3 border-top d-flex gap-2">
      <button
        type="button"
        class="btn btn-outline-secondary rounded-3 px-4 flex-fill"
        :disabled="submitting"
        @click="handleClose"
      >
        <i class="fa-solid fa-arrow-left me-1"></i> {{ inline ? $t('Back') : $t('Cancel') }}
      </button>
      <button
        type="button"
        class="btn btn-warning rounded-3 px-4 fw-bold flex-fill"
        :disabled="!canConfirm || submitting"
        @click="handleConfirm"
      >
        <i class="fa-solid fa-spinner fa-spin me-1" v-if="submitting"></i>
        <i class="fa-solid fa-check me-1" v-else></i>
        {{ $t('Confirm Split') }}
        <span v-if="newBillItemCount > 0" class="ms-1">
          ({{ newBillItemCount }} {{ $t('items') }})
        </span>
      </button>
    </div>
  </div>
</template>

<script>
import { useScrollRestore } from '@/composables/useScrollRestore';

export default {
  name: 'OffcanvasSplitBill',
  props: {
    sessionName: { type: String, default: '' },
    items: { type: Array, required: true },
    inline: { type: Boolean, default: false },
    // When set, the parent owns the submit lifecycle and shows a spinner here.
    submitting: { type: Boolean, default: false }
  },
  data() {
    return {
      rows: []
    };
  },
  computed: {
    // Rows the user can actually act on (cancelled items are read-only).
    splittableRowCount() {
      return this.rows.filter(r => !r.cancelled).length;
    },
    // Quantities currently staying on / moving to the new bill.
    originalItemCount() {
      return this.rows.reduce((sum, r) => sum + (r.cancelled ? 0 : (r.totalQty - r.newQty)), 0);
    },
    newBillItemCount() {
      return this.rows.reduce((sum, r) => sum + (r.cancelled ? 0 : r.newQty), 0);
    },
    originalSubtotal() {
      return this.rows
        .filter(r => !r.cancelled)
        .reduce((sum, r) => sum + (r.unit_price * (r.totalQty - r.newQty)), 0);
    },
    newBillSubtotal() {
      return this.rows
        .filter(r => !r.cancelled)
        .reduce((sum, r) => sum + (r.unit_price * r.newQty), 0);
    },
    canConfirm() {
      // Need at least one item moving; original may go to zero (API allows it
      // and soft-deletes the source rows).
      return this.newBillItemCount > 0;
    },
    canMoveAll() {
      return this.rows.some(r => !r.cancelled && r.newQty < r.totalQty);
    }
  },
  methods: {
    initRows() {
      this.rows = this.items.map(item => ({
        name: item.name,
        unit_price: item.unit_price,
        totalQty: item.quantity,
        newQty: 0,
        cancelled: item.cancelled,
        modifiers: item.modifiers || [],
        _source_id: item._source_id,
      }));
    },
    moveUnit(index, direction) {
      const row = this.rows[index];
      if (row.cancelled) return;
      if (direction > 0 && row.newQty < row.totalQty) {
        row.newQty++;
      } else if (direction < 0 && row.newQty > 0) {
        row.newQty--;
      }
    },
    moveAllToNew() {
      this.rows.forEach(r => {
        if (r.cancelled) return;
        r.newQty = r.totalQty;
      });
    },
    resetAll() {
      this.rows.forEach(r => {
        if (r.cancelled) return;
        r.newQty = 0;
      });
    },
    handleClose() {
      if (this.inline) {
        this.$emit('back');
      } else {
        $(this.$refs.offcanvas).offcanvas('hide');
      }
    },
    handleConfirm() {
      if (!this.canConfirm) return;
      // Build the exact PUT body the backend expects:
      //   { items: [{ id, quantity }, ...] }
      const items = this.rows
        .filter(r => !r.cancelled && r.newQty > 0 && r._source_id != null)
        .map(r => ({ id: r._source_id, quantity: r.newQty }));
      if (items.length === 0) return;
      this.$emit('confirmed', { items });
    },
    // Parent calls this after a successful API response to dismiss the panel.
    close() {
      if (this.inline) {
        this.$emit('back');
      } else {
        $(this.$refs.offcanvas).offcanvas('hide');
      }
    }
  },
  mounted() {
    this.initRows();
    if (this.inline) return;
    $(this.$refs.offcanvas).offcanvas('show');
    $(this.$refs.offcanvas).on('hidden.bs.offcanvas', () => {
      this.$emit('close');
    });
    useScrollRestore(() => this.$refs.offcanvas?.querySelector('.offcanvas-body'));
  }
};
</script>
